import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    CreditCard, ExternalLink, AlertTriangle, Check, Sparkles, Crown, TrendingUp,
    DollarSign, Zap, RefreshCw, Info, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/* ==========================================================================
   Sprint J-2 · /app/billing (in-app billing surface)
   ==========================================================================
   • Consumes new J-2 endpoints:
       GET  /api/billing/subscription   — plan + status
       GET  /api/billing/catalog        — full pricing (term × tier)
       POST /api/billing/checkout       — subscribe (Stripe)
       POST /api/billing/portal         — portal session
     Plus legacy Sprint G endpoints:
       GET  /api/billing/status         — invoice history
       GET  /api/billing/limits         — caps + usage bars
   • Platform Owner sees a lifetime-access banner and no checkout CTAs.
   • Graceful degradation when Stripe isn't configured (Coming Soon UI).
   ========================================================================== */

const TERM_META = {
    monthly: { key: "monthly", label: "Monthly",  short: "/mo",  badge: null },
    "6month": { key: "6month", label: "6 months", short: "/6mo", badge: "Save 30%" },
    annual:  { key: "annual",  label: "Annual",   short: "/yr",  badge: "Save 35%" },
};

const BUNDLE_PITCH = {
    starter:    "Solo operators, small teams",
    pro:        "Departments",
    business:   "Mid-market cross-functional",
    enterprise: "Multi-department orgs",
    enterprise_plus: "Custom SLAs, unlimited members",
};

export default function Billing() {
    const { user } = useAuth();
    const location = useLocation();
    const nav = useNavigate();

    const [subscription, setSubscription] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [status, setStatus] = useState(null);
    const [limits, setLimits] = useState(null);
    const [term, setTerm] = useState("monthly");
    const [portalBusy, setPortalBusy] = useState(false);
    const [checkoutBusy, setCheckoutBusy] = useState(null);

    const isOwner = user?.role === "platform_owner" || subscription?.owner;
    const stripeConfigured = subscription?.stripe_configured ?? false;
    // Sprint J-4-E · hide the test-mode pill in production even if the backend
    // reports test_mode=true (e.g. during the 48h test/live overlap after launch).
    const launchMode = (process.env.REACT_APP_LAUNCH_MODE || "").toLowerCase();
    const testMode = (subscription?.test_mode ?? false) && launchMode !== "production";

    const load = useCallback(async () => {
        try {
            const [sub, cat, st, lim] = await Promise.all([
                api.get("/billing/subscription").then((r) => r.data).catch(() => null),
                api.get("/billing/catalog/me").then((r) => r.data).catch(() => null),
                api.get("/billing/status").then((r) => r.data).catch(() => null),
                api.get("/billing/limits").then((r) => r.data).catch(() => null),
            ]);
            setSubscription(sub);
            setCatalog(cat);
            setStatus(st);
            setLimits(lim);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load billing");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const startCheckout = useCallback(async (sku) => {
        if (!sku) return;
        if (isOwner) {
            toast.info("Platform Owner has lifetime access — no checkout required.");
            return;
        }
        if (!stripeConfigured) {
            toast.info("Test-mode Stripe wiring pending. Please try again shortly.");
            return;
        }
        setCheckoutBusy(sku);
        try {
            const sp = new URLSearchParams(window.location.search);
            const modalSource = sp.get("source");   // 'paywall_modal' when routed from a gate
            const modalFeature = sp.get("feature"); // eg 'orchestration_run'
            const { data } = await api.post("/billing/checkout", {
                sku, quantity: 1, origin_url: window.location.origin,
                attribution: {
                    source: modalSource || "billing_upgrade_grid",
                    feature_gate: modalFeature || null,
                    upgrade_sku_hint: sku,
                    referrer: document.referrer || null,
                    term,
                },
            });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            if (data?.coming_soon) {
                toast.info(data.message || "Coming soon.");
            } else if (data?.owner_bypass) {
                toast.info(data.message || "Owner bypass.");
            } else {
                toast.info("Checkout not available.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
        } finally {
            setCheckoutBusy(null);
        }
    }, [isOwner, stripeConfigured]);

    // Handle return from Stripe + ?plan= deep-link from Pricing/Signup.
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const checkout = sp.get("checkout");
        if (checkout === "success") {
            toast.success("Checkout complete — welcome to your new plan.");
            nav("/app/billing", { replace: true });
            // Reload to reflect new state
            setTimeout(load, 800);
            return;
        }
        if (checkout === "cancelled") {
            toast.info("Checkout cancelled. Your plan is unchanged.");
            nav("/app/billing", { replace: true });
            return;
        }
        const planSku = sp.get("plan");
        if (planSku && subscription && !isOwner) {
            // Only auto-trigger once — clean the URL first.
            nav("/app/billing", { replace: true });
            startCheckout(planSku);
        }
    }, [location.search, subscription, isOwner, nav, load, startCheckout]);

    const openPortal = async () => {
        setPortalBusy(true);
        try {
            const { data } = await api.post("/billing/portal", {
                return_url: window.location.origin,
            });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            if (data?.owner_bypass) {
                toast.info(data.message || "Owner — no portal needed.");
            } else if (data?.coming_soon) {
                toast.info(data.message || "Portal launches soon.");
            } else {
                toast.info(data?.message || "Portal not available.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Portal request failed");
        } finally {
            setPortalBusy(false);
        }
    };

    if (!subscription || !status || !limits) {
        return (
            <div className="h-full grid place-items-center text-sm" style={{ color: "var(--atlas-text-muted)" }} data-testid="billing-loading">
                Loading billing…
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="billing-root">
            <div className="max-w-[1200px] mx-auto px-8 py-8 space-y-8">
                <BillingHeader
                    isOwner={isOwner}
                    subscription={subscription}
                    testMode={testMode}
                    stripeConfigured={stripeConfigured}
                />

                {!stripeConfigured && !isOwner && <StripeNotConfiguredBanner />}

                {isOwner && <OwnerBanner />}

                <CurrentPlanCard
                    subscription={subscription}
                    isOwner={isOwner}
                    stripeConfigured={stripeConfigured}
                    onOpenPortal={openPortal}
                    portalBusy={portalBusy}
                />

                <UsageSection limits={limits} unlimited={isOwner || limits.unlimited} />

                {status.invoices?.length > 0 && <InvoicesSection invoices={status.invoices} />}
                {status.invoices?.length === 0 && (
                    <div className="atlas-card p-6 text-center text-sm" style={{ color: "var(--atlas-text-muted)" }} data-testid="invoices-empty">
                        {isOwner || subscription?.plan_status === "owner_unlimited"
                            ? "No invoices — Platform Owner has lifetime access."
                            : "No invoices yet."}
                    </div>
                )}

                {!isOwner && (
                    <UpgradeSection
                        catalog={catalog}
                        term={term}
                        setTerm={setTerm}
                        subscription={subscription}
                        stripeConfigured={stripeConfigured}
                        checkoutBusy={checkoutBusy}
                        onSubscribe={startCheckout}
                        onRefresh={load}
                    />
                )}
            </div>
        </div>
    );
}

/* -------------------------- Header -------------------------- */

function BillingHeader({ isOwner, subscription, testMode, stripeConfigured }) {
    return (
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div>
                <div className="atlas-label">Billing</div>
                <h1 className="mt-1 text-3xl font-bold tracking-tight" style={{ color: "var(--atlas-text-primary)" }}>
                    Your subscription
                </h1>
                <p className="mt-1 text-sm" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Manage your plan, invoices, and Workforce Member usage.
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {testMode && (
                    <span className="atlas-pill atlas-pill--warning" data-testid="billing-test-mode-pill">
                        <Sparkles className="w-3 h-3" strokeWidth={2} /> Test mode
                    </span>
                )}
                {stripeConfigured ? (
                    <span className="atlas-pill atlas-pill--success" data-testid="billing-stripe-status">
                        <Check className="w-3 h-3" strokeWidth={2.5} /> Stripe wired
                    </span>
                ) : (
                    <span className="atlas-pill atlas-pill--warning" data-testid="billing-stripe-status">
                        <Info className="w-3 h-3" strokeWidth={2} /> Stripe wiring pending
                    </span>
                )}
                {isOwner && (
                    <span className="atlas-pill atlas-pill--primary" data-testid="billing-owner-chip">
                        <Crown className="w-3 h-3" strokeWidth={2} /> Platform Owner · Lifetime Access
                    </span>
                )}
            </div>
        </header>
    );
}

/* -------------------------- Banners -------------------------- */

function OwnerBanner() {
    return (
        <div className="atlas-card p-5 flex items-start gap-3" data-testid="billing-owner-banner"
             style={{ borderColor: "rgba(139,92,246,0.5)", background: "var(--atlas-primary-muted)" }}>
            <Crown className="w-5 h-5 mt-0.5" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
            <div>
                <div className="text-[14px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                    Platform Owner · Lifetime Access
                </div>
                <div className="text-[13px] mt-1 leading-relaxed" style={{ color: "var(--atlas-text-tertiary)" }}>
                    All bundles, à la carte activations, and Skill Packs are unlocked at no cost. Stripe checkout is bypassed for your account by design.
                </div>
            </div>
        </div>
    );
}

function StripeNotConfiguredBanner() {
    return (
        <div className="atlas-card p-4 flex items-start gap-3" data-testid="billing-not-configured"
             style={{ borderColor: "rgba(245,158,11,0.45)", background: "rgba(245,158,11,0.10)" }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={2} style={{ color: "#fbbf24" }} />
            <div>
                <div className="text-[14px] font-semibold" style={{ color: "#fbbf24" }}>
                    Test-mode Stripe wiring pending
                </div>
                <div className="text-[13px] mt-1 leading-relaxed" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Checkout is temporarily unavailable while we finalize the Stripe sandbox. Your workspace continues on its current plan; no action needed.
                </div>
            </div>
        </div>
    );
}

/* -------------------------- Current plan -------------------------- */

function CurrentPlanCard({ subscription, isOwner, stripeConfigured, onOpenPortal, portalBusy }) {
    const planSku = subscription.plan_sku || "starter";
    const planStatus = subscription.plan_status || "self_serve";
    const seatsAllowance = subscription.member_seats_allowance;
    const cpe = subscription.current_period_end
        ? new Date(subscription.current_period_end).toLocaleDateString()
        : null;
    return (
        <section className="atlas-card p-6" data-testid="billing-current-plan">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="atlas-label">Current plan</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="atlas-pill atlas-pill--primary" data-testid="billing-plan-sku">
                            {planSku || "starter"}
                        </span>
                        <span className={`atlas-pill ${planStatus === "active" ? "atlas-pill--success" : ""}`} data-testid="billing-plan-status">
                            {planStatus}
                        </span>
                        {isOwner && (
                            <span className="atlas-pill atlas-pill--primary">
                                <Sparkles className="w-3 h-3" strokeWidth={2} /> Unlimited
                            </span>
                        )}
                    </div>
                    <div className="mt-3 text-sm" style={{ color: "var(--atlas-text-secondary)" }}>
                        {isOwner ? (
                            "No renewal — lifetime access is not tied to a billing cycle."
                        ) : cpe ? (
                            <>Next invoice: <span className="font-medium" style={{ color: "var(--atlas-text-primary)" }}>{cpe}</span></>
                        ) : (
                            "No active Stripe subscription."
                        )}
                    </div>
                    {seatsAllowance != null && (
                        <div className="mt-1.5 text-sm" style={{ color: "var(--atlas-text-secondary)" }}>
                            Workforce Member allowance: <span className="font-medium" style={{ color: "var(--atlas-text-primary)" }}>
                                {seatsAllowance === "unlimited" ? "Unlimited" : `${seatsAllowance} seats`}
                            </span>
                        </div>
                    )}
                </div>
                {!isOwner && (
                    <div className="flex flex-col gap-2 items-end">
                        <button
                            onClick={onOpenPortal}
                            disabled={portalBusy || !stripeConfigured || !subscription.stripe_customer_id}
                            data-testid="billing-portal-btn"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <CreditCard className="w-4 h-4" strokeWidth={2} />
                            {portalBusy ? "Opening…" : "Manage billing"}
                            <ExternalLink className="w-3 h-3" strokeWidth={2} />
                        </button>
                        <div className="text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>
                            {!stripeConfigured
                                ? "Available when Stripe is configured"
                                : subscription.stripe_customer_id
                                    ? "Opens Stripe Customer Portal"
                                    : "Subscribe first to open the portal"}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

/* -------------------------- Usage -------------------------- */

function UsageSection({ limits, unlimited }) {
    return (
        <section data-testid="billing-usage">
            <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />
                <div className="text-sm font-semibold" style={{ color: "var(--atlas-text-primary)" }}>Today's usage</div>
                {unlimited && <span className="atlas-pill atlas-pill--primary text-[9px]">UNLIMITED</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <UsageCard
                    label="Calls today"
                    icon={<Zap className="w-4 h-4" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />}
                    value={limits.usage.today.calls.toLocaleString()}
                    sub={`${limits.usage.month.calls.toLocaleString()} this month`}
                />
                <UsageCard
                    label="Tokens today"
                    icon={<Sparkles className="w-4 h-4" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />}
                    value={limits.usage.today.tokens.toLocaleString()}
                    cap={limits.caps.tokens_per_day_cap}
                    unlimited={unlimited}
                />
                <UsageCard
                    label="Spend today"
                    icon={<DollarSign className="w-4 h-4" strokeWidth={2} style={{ color: "var(--atlas-primary-hover)" }} />}
                    value={`$${limits.usage.today.cost_usd.toFixed(2)}`}
                    capValue={limits.caps.spend_per_day_cap_usd ? `$${limits.caps.spend_per_day_cap_usd}` : null}
                    raw={limits.usage.today.cost_usd}
                    capRaw={limits.caps.spend_per_day_cap_usd}
                    unlimited={unlimited}
                />
            </div>
            {!unlimited && limits.caps.spend_per_month_cap_usd && (
                <div className="mt-3 text-[11.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                    Month-to-date spend: <span className="font-semibold" style={{ color: "var(--atlas-text-secondary)" }}>${limits.usage.month.cost_usd.toFixed(2)}</span>{" "}
                    of <span className="font-medium">${limits.caps.spend_per_month_cap_usd}</span> monthly cap.
                </div>
            )}
        </section>
    );
}

function UsageCard({ label, icon, value, cap, capValue, unlimited, sub, raw, capRaw }) {
    let pct = 0;
    let pctRaw = null;
    if (!unlimited && cap) {
        pctRaw = Math.min(1, (Number(String(value).replace(/[^\d.]/g, "")) || 0) / cap);
        pct = Math.round(pctRaw * 100);
    } else if (!unlimited && capRaw && raw !== undefined) {
        pctRaw = Math.min(1, raw / capRaw);
        pct = Math.round(pctRaw * 100);
    }
    const warn = pct >= 80;
    return (
        <div className="atlas-card p-4" data-testid={`usage-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
                <div className="atlas-label inline-flex items-center gap-1.5">{icon} {label}</div>
                {warn && !unlimited && <span className="atlas-pill atlas-pill--warning text-[9px]">80%+</span>}
            </div>
            <div className="mt-2 text-2xl font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>{value}</div>
            {unlimited ? (
                <div className="mt-1 text-[11px] font-semibold" style={{ color: "#34d399" }}>No cap</div>
            ) : cap ? (
                <>
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--atlas-bg-highlight)" }}>
                        <div
                            className="h-full"
                            style={{
                                width: `${Math.min(100, pct)}%`,
                                background: warn ? "#fbbf24" : "var(--atlas-primary)",
                            }}
                        />
                    </div>
                    <div className="mt-1 text-[11px] tabular-nums" style={{ color: "var(--atlas-text-muted)" }}>
                        {pct}% of {cap.toLocaleString()} cap
                    </div>
                </>
            ) : capValue ? (
                <>
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--atlas-bg-highlight)" }}>
                        <div
                            className="h-full"
                            style={{
                                width: `${Math.min(100, pct)}%`,
                                background: warn ? "#fbbf24" : "var(--atlas-primary)",
                            }}
                        />
                    </div>
                    <div className="mt-1 text-[11px] tabular-nums" style={{ color: "var(--atlas-text-muted)" }}>
                        {pct}% of {capValue} cap
                    </div>
                </>
            ) : sub ? (
                <div className="mt-2 text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{sub}</div>
            ) : null}
        </div>
    );
}

/* -------------------------- Invoices -------------------------- */

function cents(n) { return `$${((n || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`; }

function InvoicesSection({ invoices }) {
    return (
        <section data-testid="billing-invoices">
            <div className="mb-3 flex items-center gap-2">
                <div className="text-sm font-semibold" style={{ color: "var(--atlas-text-primary)" }}>Invoice history</div>
            </div>
            <div className="atlas-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead style={{ background: "var(--atlas-bg-elevated)", color: "var(--atlas-text-muted)" }} className="text-[11px] uppercase tracking-widest">
                        <tr>
                            <th className="text-left px-4 py-2 font-medium">Invoice</th>
                            <th className="text-left px-4 py-2 font-medium">Date</th>
                            <th className="text-left px-4 py-2 font-medium">Status</th>
                            <th className="text-right px-4 py-2 font-medium">Amount</th>
                            <th className="text-right px-4 py-2 font-medium"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: "var(--atlas-border)" }}>
                        {invoices.map((inv) => (
                            <tr key={inv.id} data-testid={`invoice-${inv.id}`}>
                                <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--atlas-text-secondary)" }}>{inv.number || inv.id}</td>
                                <td className="px-4 py-2 text-xs" style={{ color: "var(--atlas-text-muted)" }}>
                                    {new Date(inv.created * 1000).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-2">
                                    <span className={`atlas-pill text-[10px] ${inv.status === "paid" ? "atlas-pill--success" : ""}`}>
                                        {inv.status}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>{cents(inv.amount_paid_cents)}</td>
                                <td className="px-4 py-2 text-right">
                                    {inv.hosted_invoice_url && (
                                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                                           style={{ color: "var(--atlas-primary-hover)" }}
                                           className="text-xs inline-flex items-center gap-1 hover:opacity-90">
                                            View <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

/* -------------------------- Upgrade / switch -------------------------- */

function UpgradeSection({ catalog, term, setTerm, subscription, stripeConfigured, checkoutBusy, onSubscribe, onRefresh }) {
    if (!catalog) return null;
    const bundles = (catalog.bundles || []).filter((b) => b.prices);
    const currentSku = subscription.plan_sku;
    return (
        <section data-testid="billing-upgrade-options">
            <div className="mb-3 flex items-center gap-2 flex-wrap">
                <div className="text-sm font-semibold" style={{ color: "var(--atlas-text-primary)" }}>Upgrade or switch bundle</div>
                <div className="ml-auto flex items-center gap-1 rounded-full p-1"
                     style={{ background: "var(--atlas-bg-elevated)", border: "1px solid var(--atlas-border-strong)" }}
                     data-testid="billing-term-toggle">
                    {["monthly", "6month", "annual"].map((t) => {
                        const meta = TERM_META[t];
                        const active = t === term;
                        return (
                            <button
                                key={t}
                                onClick={() => setTerm(t)}
                                data-testid={`billing-term-${t}`}
                                className="px-3 h-8 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5"
                                style={active ? {
                                    background: "var(--atlas-gradient-btn)",
                                    color: "#fff",
                                } : {
                                    background: "transparent",
                                    color: "var(--atlas-text-tertiary)",
                                }}
                            >
                                {meta.label}
                                {meta.badge && !active && (
                                    <span className="text-[9px] font-semibold px-1 rounded-full"
                                          style={{ background: "var(--atlas-success-muted)", color: "#34d399" }}>
                                        {meta.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={onRefresh}
                    className="atlas-btn-ghost inline-flex items-center gap-1 rounded-md px-2 h-7 text-[11px]"
                    data-testid="billing-refresh"
                >
                    <RefreshCw className="w-3 h-3" strokeWidth={2} /> Refresh
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {bundles.map((b) => {
                    const priceRow = b.prices[term];
                    if (!priceRow) return null;
                    const isCurrent = currentSku === priceRow.sku;
                    const busy = checkoutBusy === priceRow.sku;
                    return (
                        <div
                            key={b.tier}
                            data-testid={`upgrade-${b.tier}`}
                            className={`atlas-card p-4 flex flex-col ${isCurrent ? "atlas-card--elevated" : ""}`}
                            style={isCurrent ? { borderColor: "var(--atlas-primary)" } : undefined}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-[13px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{b.name}</div>
                                {isCurrent && <span className="atlas-pill atlas-pill--primary text-[9px]">Current</span>}
                            </div>
                            <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>
                                ${priceRow.total_usd.toLocaleString()}
                                <span className="text-sm font-normal" style={{ color: "var(--atlas-text-muted)" }}>
                                    {TERM_META[term].short}
                                </span>
                            </div>
                            {term !== "monthly" && (
                                <div className="text-[11px] tabular-nums" style={{ color: "var(--atlas-text-muted)" }}>
                                    ≈ ${priceRow.effective_monthly_usd.toLocaleString()}/mo effective
                                </div>
                            )}
                            <div className="mt-1 text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{b.seats} Workforce Members</div>
                            <div className="mt-1 text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{BUNDLE_PITCH[b.tier] || ""}</div>
                            <button
                                onClick={() => onSubscribe(priceRow.sku)}
                                disabled={isCurrent || busy || !stripeConfigured}
                                data-testid={`upgrade-cta-${b.tier}`}
                                className={`mt-4 inline-flex items-center justify-center gap-1 rounded-lg h-9 text-xs font-semibold ${
                                    isCurrent ? "" : "atlas-btn-primary"
                                }`}
                                style={isCurrent ? {
                                    background: "var(--atlas-bg-highlight)",
                                    color: "var(--atlas-text-muted)",
                                    border: "1px solid var(--atlas-border-strong)",
                                    cursor: "not-allowed",
                                } : undefined}
                            >
                                {isCurrent ? (
                                    <><Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Current plan</>
                                ) : !stripeConfigured ? (
                                    "Coming Soon"
                                ) : busy ? (
                                    "Opening checkout…"
                                ) : (
                                    <>Choose <ArrowRight className="w-3 h-3" strokeWidth={2} /></>
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
            {!stripeConfigured && (
                <div className="mt-3 text-[11.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                    Buttons will unlock once Stripe test-mode wiring is complete. Reach out to sales in the meantime.
                </div>
            )}
        </section>
    );
}
